import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, XCircle, Loader2 } from "lucide-react";
import * as api from "@/src/api/sdk.gen";

/**
 * Public certificate verification — an employer holding a printed certificate has
 * to be able to check it without an inaraX account, so this route sits outside
 * ProtectedRoute and the request carries no session.
 */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-lg">
        <Link to="/" className="mb-8 flex justify-center" aria-label="inaraX home">
          <img src="/images/logo-v2.png" alt="inaraX" className="h-8 w-auto" />
        </Link>
        {children}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-slate-100 py-3 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:w-40">
        {label}
      </dt>
      <dd className="text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

export default function VerifyCertificatePage() {
  const { code = "" } = useParams<{ code: string }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ["certificate-verify", code],
    queryFn: async () => {
      const { data, error, response } = await api.verifyCertificateApiV1CertificatesVerifyCodeGet({
        // No decodeURIComponent: useParams has already decoded this, and a second
        // pass throws URIError on a stray "%" — turning a typo into the generic
        // "no certificate matches" page instead of a format complaint.
        path: { code },
      });
      // A 404 is a verdict about the code; anything else is our problem, and
      // must not be reported to the visitor as "this certificate is not genuine".
      if (response?.status === 404) return null;
      if (error || !data) throw new Error("verification unavailable");
      return data;
    },
    enabled: code.trim().length > 0,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (isPending && code.trim().length > 0) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Checking this credential…
        </div>
      </Shell>
    );
  }

  // One answer for a malformed code, an unknown code, and a revoked or expired
  // one. Anything more specific turns this page into a way to probe which
  // credential IDs exist.
  // Distinguish "we could not check" from "this code is not valid".
  if (isError) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden />
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                Verification is temporarily unavailable
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                This does not mean the certificate is invalid — we could not reach the
                verification service. Please try again shortly.
              </p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden />
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                No certificate matches this code
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Check the credential ID printed on the certificate and try again. It looks like
                <span className="font-mono text-slate-800"> INX-1011-66B2-04B3</span>.
              </p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // `completion_date` is a naive datetime, which JS parses as LOCAL time. Format
  // it as UTC without appending "Z" and the rendered day shifts either side of
  // midnight, so the page would contradict the date printed on the certificate.
  const raw = String(data.completion_date);
  const asUtc = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw) ? raw : `${raw}Z`;
  const completed = new Date(asUtc).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  return (
    <Shell>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-start gap-3 border-b border-emerald-100 bg-emerald-50 p-6 sm:p-8">
          <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden />
          <div>
            <h1 className="text-lg font-bold text-emerald-900">This certificate is genuine</h1>
            <p className="mt-1 text-sm text-emerald-800">Issued by inaraX and currently valid.</p>
          </div>
        </div>

        <dl className="p-6 sm:p-8">
          {/* Omitted when only an email is on file — see holder_name_for_display. */}
          {data.holder_name ? <DetailRow label="Awarded to" value={data.holder_name} /> : null}
          <DetailRow label="Course" value={data.course_name} />
          <DetailRow label="Level" value={data.level_name} />
          <DetailRow label="Completed" value={completed} />
          <DetailRow label="Credential ID" value={data.credential_id} />
        </dl>
      </div>

      <p className="mt-6 text-center text-xs text-slate-500">
        Showing only what appears on the certificate itself. inaraX does not publish any other
        details of the holder.
      </p>
    </Shell>
  );
}
